import { useCallback, useState } from "react";

import { CommandPalette } from "@/components/command-palette";
import { ShortcutsHelp } from "@/components/shortcuts-help";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useNavigationShortcuts } from "@/hooks/use-navigation-shortcuts";

/**
 * Mounts the global keyboard layer for the v2 app:
 *  - command palette (⌘K, /)
 *  - keyboard shortcut sequences (G H/C/O/J/I/S)
 *  - shortcuts help panel (?)
 *  - shadcn TooltipProvider so sidebar tooltips render with low delay.
 *
 * Wraps the v2 app at the AppLayoutV2 level — see App.tsx route declarations.
 */
export function KeyboardShortcutsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const openPalette = useCallback(() => {
    setHelpOpen(false);
    setPaletteOpen(true);
  }, []);
  const openHelp = useCallback(() => {
    setPaletteOpen(false);
    setHelpOpen(true);
  }, []);

  useNavigationShortcuts({
    onOpenCommand: openPalette,
    onOpenShortcutsHelp: openHelp,
    onOpenSearch: openPalette,
  });

  return (
    <TooltipProvider delayDuration={300}>
      {children}
      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        onOpenShortcutsHelp={openHelp}
      />
      <ShortcutsHelp open={helpOpen} onOpenChange={setHelpOpen} />
    </TooltipProvider>
  );
}
